import type { Report, ServiceItem, ServiceTransaction } from '../../types';
import type { JournalMirrorRequest } from '../../services/serviceJournal';
import { hatBestandteile } from '../../services/bestandteile/bestandteilLogic';
import type { Dokumentart } from './kapitel';
import { DOKUMENTE } from './kapitel';
import type { Bauordnung } from './types';

/**
 * Die Buchung einer Baustellenordnung bzw. eines HSE-Plans auf eine
 * LV-Position — als reine Rechenfunktion neben der Oberflaeche
 * (Spezifikation J; Vorbild `services/einweisungBooking.ts`).
 *
 * ═══ WANN ═════════════════════════════════════════════════════════════════
 * Nach der FREIGABE, je Dokument genau einmal, und nie beim Export (J.2). Der
 * Export ist ein Druckvorgang; wer dort buchte, buchte bei jeder Korrektur-
 * fassung erneut. Ausgeloest wird die Buchung nur per Klick — der Riegel
 * gegen die zweite Buchung ist `markiereGebucht` im Dienst (J.4), die
 * Bedingung steht dort IM UPDATE.
 *
 * ═══ WELCHE POSITION ══════════════════════════════════════════════════════
 * Beide Dokumente tragen `assignment: 'site_rules'`. Die Aufloesung laeuft
 * deshalb NICHT ueber die Zuordnung — `find(s => s.assignment === 'site_rules')`
 * traefe je nach Sortierung die falsche der beiden Positionen (J.3) — sondern:
 *   1. ueber die Katalogkennung (`id '5'` Ordnung, `id '8'` HSE-Plan),
 *   2. sonst waehlt der Anwender einmal aus den `site_rules`-Positionen,
 *   3. sonst wird nicht gebucht und nicht gescheitert.
 * Positionsnummern sind KEIN Kriterium: die Nummerierung gehoert dem Kunden.
 *
 * ═══ MENGE ════════════════════════════════════════════════════════════════
 * 1 je Dokument (Einheit „Pauschal"). Eine Revision bucht nicht erneut.
 *
 * Rein — die Funktion liest und schreibt nichts, sie liefert die neue
 * Leistungsliste und den Protokolleintrag; der Aufrufer legt beides ueber
 * seinen `onUpdate`-Pfad ab.
 */

export type ServiceLogEntry = ServiceTransaction;

/** Katalogkennung der Standardposition je Dokument (`SERVICE_CATALOG`, Spezifikation J.1). */
export const KATALOG_ID: Readonly<Record<Dokumentart, string>> = { ordnung: '5', hse: '8' };

/** Die Zuordnung, die beide Positionen tragen. */
export const SITE_RULES = 'site_rules';

export const GEBUCHT_AM_SPALTE: Readonly<Record<Dokumentart, 'gebucht_ordnung_am' | 'gebucht_hse_am'>> = {
    ordnung: 'gebucht_ordnung_am',
    hse: 'gebucht_hse_am',
};

export const GEBUCHTE_POSITION_SPALTE: Readonly<Record<Dokumentart, 'gebuchte_ordnung_position_id' | 'gebuchte_hse_position_id'>> = {
    ordnung: 'gebuchte_ordnung_position_id',
    hse: 'gebuchte_hse_position_id',
};

export const GEBUCHTE_MENGE_SPALTE: Readonly<Record<Dokumentart, 'gebuchte_ordnung_menge' | 'gebuchte_hse_menge'>> = {
    ordnung: 'gebuchte_ordnung_menge',
    hse: 'gebuchte_hse_menge',
};

/** Ist DIESES Dokument schon gebucht? Liest nur das Spaltenpaar des Dokuments. */
export const istGebucht = (o: Pick<Bauordnung, 'gebucht_ordnung_am' | 'gebucht_hse_am'>, art: Dokumentart): boolean =>
    !!o[GEBUCHT_AM_SPALTE[art]];

export interface PositionsAuswahl {
    /** Ueber die Katalogkennung gefunden — Regelfall beim Standardkatalog. */
    vorgeschlagen: ServiceItem | null;
    /** Alle `site_rules`-Positionen des Projekts — zur Wahl, wenn die Kennung nichts findet. */
    auswahl: ServiceItem[];
}

/**
 * Welche LV-Position fuer ein Dokument in Frage kommt (J.3).
 *
 * `vorgeschlagen` ist die Position mit der Katalogkennung; `auswahl` fuehrt
 * daneben jede `site_rules`-Position — auch die vorgeschlagene —, damit der
 * Anwender bei einem importierten Kunden-LV sieht, worunter er waehlt.
 *
 * Eine PAKETPOSITION (mit Bestandteilen) ist nie dabei, auch nicht als
 * Katalogtreffer: sie wird nur gebucht, wenn ihr letzter Bestandteil erledigt
 * ist (Plan Kernregel 7, bestandteilBuchung) — die Freigabe erledigt hier den
 * Bestandteil „Baustellenordnung" per Trigger, gebucht wird das Paket dann
 * vom Abschlussschritt, nie von dieser Zeile.
 */
export const positionenFuer = (services: readonly ServiceItem[] | null | undefined, art: Dokumentart): PositionsAuswahl => {
    const liste = (services ?? []).filter(s => !!s && !hatBestandteile(s));
    return {
        vorgeschlagen: liste.find(s => s.id === KATALOG_ID[art]) ?? null,
        auswahl: liste.filter(s => s.assignment === SITE_RULES),
    };
};

/** Die eigenen Positionen ALLER erzeugten Dokumente — fuer den Fall des Abschlussschritts (Plan B5). */
export const eigenePositionen = (
    services: readonly ServiceItem[] | null | undefined,
    dokumente: readonly Dokumentart[],
): ServiceItem[] => {
    const out: ServiceItem[] = [];
    for (const art of dokumente) {
        const p = positionenFuer(services, art);
        for (const s of [p.vorgeschlagen, ...p.auswahl]) {
            if (s && !out.some(x => x.id === s.id)) out.push(s);
        }
    }
    return out;
};

/** Was der Protokolltext ueber das gebuchte Dokument sagen soll. */
export interface BauordnungBuchungsKontext {
    dokument: Dokumentart;
    /** Fassungsbezeichnung der freigegebenen Fassung (`version_label`). */
    versionLabel?: string | null;
    /** Freigabezeitpunkt, ISO — Teil des Textes, damit die Buchung einer Fassung zuordenbar bleibt. */
    freigegebenAm?: string | null;
    /** Anzeigename des Buchenden; ohne ihn steht „System" im Protokoll. */
    performerName?: string;
}

export interface BauordnungBuchung {
    /** Die vollstaendige Leistungsliste MIT der gebuchten Position. */
    services: ServiceItem[];
    /** Der Eintrag, der an `globalData.serviceLog` angehaengt wird. */
    logEntry: ServiceLogEntry;
}

/** Menge je Dokument — Pauschale. */
export const MENGE_JE_DOKUMENT = 1;

/** ISO → TT.MM.JJJJ durch Zerlegen; kein `new Date`, der Tag soll nicht wandern. */
const tagDeutsch = (iso?: string | null): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso || '').trim());
    return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

/**
 * Der Protokolltext. Er nennt WELCHES Dokument gebucht wurde — sonst stehen
 * spaeter zwei nicht unterscheidbare Zeilen im Leistungsprotokoll (J.4) —
 * sowie Fassung und Freigabetag.
 */
export const buchungsText = (kontext: BauordnungBuchungsKontext, menge: number, unit: string): string => {
    const teile: string[] = [];
    const fassung = (kontext.versionLabel || '').trim();
    teile.push(fassung ? `Fassung ${fassung}` : 'ohne Fassungsbezeichnung');
    const tag = tagDeutsch(kontext.freigegebenAm);
    if (tag) teile.push(`freigegeben ${tag}`);
    return `${DOKUMENTE[kontext.dokument].titel} erstellt (${teile.join(', ')}) (${menge} ${unit})`;
};

/**
 * Bucht ein Dokument auf die LV-Position `serviceId`.
 *
 * `null`, wenn es die Position nicht gibt — der Aufrufer darf dann NICHT
 * schreiben. Menge ist immer 1; Status `done`, sobald das Kontingent erreicht
 * oder ueberschritten ist (Regel des Bestands). Die uebrigen Positionen —
 * auch eine zweite `site_rules`-Position — bleiben unberuehrt.
 *
 * Ebenfalls `null` fuer eine PAKETPOSITION (zweite Sperre, Plan Kernregel 7):
 * die Auswahl bietet sie schon nicht an (`positionenFuer`), aber auch eine
 * Id, die anders hierher kommt, bucht kein Paket — das bucht nur der
 * Abschlussschritt, wenn sein letzter Bestandteil erledigt ist.
 */
export const bucheBauordnung = (
    services: readonly ServiceItem[],
    serviceId: string,
    kontext: BauordnungBuchungsKontext,
): BauordnungBuchung | null => {
    const liste = services ?? [];
    const index = liste.findIndex(s => s.id === serviceId);
    if (index === -1) return null;
    if (hatBestandteile(liste[index])) return null;

    const service = liste[index];
    const menge = MENGE_JE_DOKUMENT;
    const unit = service.unit || 'Pauschal';
    const newUsed = (service.quantityUsed || 0) + menge;
    const isDone = service.quantityContracted > 0 && newUsed >= service.quantityContracted;

    const newServices = [...liste];
    newServices[index] = {
        ...service,
        quantityUsed: newUsed,
        status: isDone ? 'done' : service.status,
    };

    const logEntry: ServiceLogEntry = {
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
        serviceId: service.id,
        quantity: menge,
        performerId: 'system',
        performerName: kontext.performerName || 'System',
        isSubstitution: false,
        description: buchungsText(kontext, menge, unit),
    };

    return { services: newServices, logEntry };
};

/**
 * Ist die gewaehlte Position eine Paketposition? Dann bucht die Zeile nicht —
 * gefragt VOR dem Riegel in der Datenbank, damit kein Dokument als gebucht
 * vermerkt wird, dessen Gutschrift danach verweigert wuerde.
 */
export const istPaketZiel = (services: readonly ServiceItem[] | null | undefined, serviceId: string): boolean =>
    (services ?? []).some(s => !!s && s.id === serviceId && hatBestandteile(s));

/**
 * Was nach dem Riegel (`markiereGebucht` = true) geschieht — die Entscheidung,
 * rein (Codex R2c):
 *  - 'gutschreiben': der Schreibweg ist frei (`sperre` null) und die Position
 *    steht im Stand (`planGefunden`);
 *  - 'zuruecknehmen': sonst. Der Vermerk steht dann in der Datenbank, die
 *    Gutschrift aber nicht im Projekt — er wird bedingt zurueckgenommen
 *    (`nimmGebuchtZurueck`), damit ein neuer Versuch moeglich bleibt.
 *  - Auch eine VERWORFENE Gutschrift (`verworfen`, der Schreibweg hat beim
 *    Schreiben abgelehnt) heisst 'zuruecknehmen'.
 */
export type NachRiegel = 'gutschreiben' | 'zuruecknehmen';

export const entscheideNachRiegel = (a: {
    sperre: string | null;
    planGefunden: boolean;
    verworfen?: string | null;
}): NachRiegel =>
    (!a.sperre && a.planGefunden && !a.verworfen ? 'gutschreiben' : 'zuruecknehmen');

/**
 * Die Meldung nach einer Ruecknahme: gelungen → „abgebrochen, bitte erneut
 * buchen"; nicht gelungen → laut, denn dann gilt das Dokument als gebucht,
 * ohne dass die Leistung im Projekt steht.
 */
export const ruecknahmeMeldung = (titel: string, ruecknahme: 'zurueckgenommen' | 'fehlgeschlagen'): string =>
    ruecknahme === 'zurueckgenommen'
        ? `${titel}: Buchung abgebrochen — die Leistung konnte nicht ins Projekt geschrieben werden. Bitte erneut buchen.`
        : `${titel}: ACHTUNG — als gebucht vermerkt, aber die Leistung steht NICHT im Projekt, und der Vermerk ließ sich `
            + 'nicht zurücknehmen. Bitte den Leistungsstand prüfen und die Verwaltung informieren (Fehler ist gemeldet).';

/** Der Stand nach der Buchung und die Vormerkung der Zweitschrift — siehe `planeBauordnungBuchung`. */
export interface BauordnungBuchungsPlan {
    report: Report;
    logEntry: ServiceLogEntry;
    /** `null` ohne belastbare Projekt-id (`temp-id`): dann wird nichts vorgemerkt. */
    vormerkung: JournalMirrorRequest | null;
}

/**
 * Die Buchung als neuer Projektstand PLUS Zweitschrift ins Buchungsjournal
 * (Plan B5: „Journalquelle 'bauordnung' ergaenzen und `queueJournalMirror`
 * nachziehen"). Rein — der Aufrufer legt `report` ueber `onUpdate` ab und
 * merkt `vormerkung` danach vor (Muster B wie Vorankuendigung und
 * handleTrackService: angehaengt wird erst nach dem bestaetigten Flush).
 *
 * Bis hierhin fehlte die Zweitschrift: `JournalSource` kannte keinen Wert
 * fuer dieses Modul, die Buchung stand nur im Projekt-JSON und kam erst ueber
 * den Nachtlauf (Nr. 76) ins Journal.
 *
 * `null`, wenn der Stand kein `globalData` hat oder die Position fehlt.
 */
export const planeBauordnungBuchung = (
    report: Report,
    serviceId: string,
    kontext: BauordnungBuchungsKontext,
): BauordnungBuchungsPlan | null => {
    const globalData = report?.metadata?.globalData;
    if (!globalData) return null;
    const ergebnis = bucheBauordnung(globalData.services || [], serviceId, kontext);
    if (!ergebnis) return null;
    const previousLog = globalData.serviceLog || [];
    const projectId = (globalData.projectId || '').trim();
    return {
        report: {
            ...report,
            metadata: {
                ...report.metadata,
                globalData: { ...globalData, services: ergebnis.services, serviceLog: [...previousLog, ergebnis.logEntry] },
            },
        },
        logEntry: ergebnis.logEntry,
        vormerkung: projectId && projectId !== 'temp-id'
            ? {
                projectId,
                entries: [ergebnis.logEntry],
                previousLog,
                source: 'bauordnung',
                linkedProjectConfig: globalData.linkedProjectConfig ?? null,
                // Sicher gewusst: eine Baustellenordnung bzw. ein HSE-Plan ist
                // weder Zusatzleistung noch Nachweis einer BEGEHUNG.
                statements: { [ergebnis.logEntry.id]: { isAddon: false, coversInspection: false } },
            }
            : null,
    };
};
